"use client";

import { Dialog, Transition } from "@headlessui/react";
import { X } from "lucide-react";
import { Fragment, useState, useEffect, useId, useRef, type KeyboardEvent } from "react";
import { FetchButton } from "./FetchButton";
import { modalStack } from "./FetchModal";
import { FetchInput } from "./FetchInput";
import { FetchToggle } from "./FetchToggle";

interface FetchConfirmationDialogBaseProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when the dialog is closed without confirming */
  onClose: () => void;
  /** Called when the user confirms the action. Return `false` to keep the dialog open, e.g. when the action failed. */
  onConfirm: () => void | boolean | Promise<void | boolean>;
  /** Dialog title */
  title: string;
  /** Description content explaining the action. Accepts a plain string or rich block content (e.g. a bulleted list of reasons). */
  message: React.ReactNode;
  /**
   * Optional icon rendered to the left of the title. Caller supplies the
   * element (sized + colored as desired); a 40px circular tinted backdrop
   * wraps it for visual consistency.
   */
  icon?: React.ReactNode;
  /** Tailwind background color class for the icon backdrop (e.g. "bg-green-50"). Defaults based on `type`. */
  iconBgClass?: string;
  /**
   * Header layout. `"inline"` (default) sits the icon to the left of the title. `"stacked"` places
   * the icon above a left-aligned title + message column, better when the message is multi-line or
   * a list, where inline alignment leaves the body awkwardly indented beside the icon.
   */
  layout?: "inline" | "stacked";
  /** Whether the confirm action is in progress */
  isLoading?: boolean;
  /**
   * Disable only the confirm button, for example while the facts the confirm depends on are still
   * loading. Unlike `isLoading`, the cancel button, the X, and the backdrop still dismiss.
   */
  isConfirmDisabled?: boolean;
  /** Tailwind z-index class applied to the dialog root. Override when rendering above a drawer or other elevated surface. */
  zIndexClass?: string;
  /** A third, muted choice rendered first in the footer, for dialogs that are not a plain yes or no. */
  secondaryAction?: { label: string; onClick: () => void };
}

/** The original confirmation dialog: every prop it has always had, unchanged. */
interface FetchConfirmationDialogDefaultProps extends FetchConfirmationDialogBaseProps {
  /** `default` (or omitted): the general dialog, styled by `type`. */
  variant?: "default";
  /** Text for the confirm button */
  confirmText?: string;
  /** Text for the cancel button */
  cancelText?: string;
  /**
   * Hide the outline cancel button, leaving only the primary action in the footer. Use when the
   * primary action IS the safe/dismiss choice (e.g. a "Cancel" confirm) so the footer isn't two
   * redundant dismiss buttons. The X and backdrop still dismiss via `onClose`.
   */
  hideCancel?: boolean;
  /** Visual style of the dialog */
  type?: "danger" | "warning" | "info";
  /**
   * Which button carries the primary (filled) emphasis. Defaults to `"confirm"`.
   * Set to `"cancel"` when the safer choice is to back out: the cancel button
   * becomes the filled CTA and the confirm button becomes the muted outline.
   */
  emphasis?: "confirm" | "cancel";
  /**
   * When `emphasis="cancel"`, controls the filled cancel button's color. Defaults to
   * `"primary"` (blue "back out safely" CTA). Set `"semantic"` to use the dialog's
   * `type` color instead (e.g. red for `type="danger"`) when the emphasized action
   * should still visually carry the danger/warning context it's backing out of.
   */
  emphasizedCancelColor?: "primary" | "semantic";
  /** Whether to require a toggle confirmation before the confirm button is enabled */
  requiresToggle?: boolean;
  confirmLabel?: never;
  cancelLabel?: never;
  consequences?: never;
  typeToConfirm?: never;
}

/**
 * A confirm for an action that deletes or cannot be undone: a red primary button that names the
 * action, a ghost way back, and optionally what else goes with it and a typed confirmation.
 */
interface FetchConfirmationDialogDestructiveProps extends FetchConfirmationDialogBaseProps {
  variant: "destructive";
  /** The red button's label, verb first and naming the thing ("Delete event", "Remove 3 venues"). Required. */
  confirmLabel: string;
  /** The way back. Defaults to "Go back". */
  cancelLabel?: string;
  /** What else this deletes or changes, one short line each ("12 venue quotes", "The event's landing page"). */
  consequences?: readonly string[];
  /**
   * Text the person must type before the red button turns on, for actions too big for one click
   * (usually the record's name). Matched exactly, ignoring leading and trailing spaces.
   */
  typeToConfirm?: string;
  confirmText?: never;
  cancelText?: never;
  hideCancel?: never;
  type?: never;
  emphasis?: never;
  emphasizedCancelColor?: never;
  requiresToggle?: never;
}

/** Props for {@link FetchConfirmationDialog}. */
export type FetchConfirmationDialogProps =
  | FetchConfirmationDialogDefaultProps
  | FetchConfirmationDialogDestructiveProps;

type DialogType = NonNullable<FetchConfirmationDialogDefaultProps["type"]>;

const BUTTON_COLOR_MAP = {
  danger: "destructive" as const,
  warning: "warning" as const,
  info: "primary" as const,
};

const ICON_BG_BY_TYPE: Record<DialogType, string> = {
  danger: "bg-red-50",
  warning: "bg-yellow-50",
  info: "bg-indigo-50",
};

/** The destructive variant's way back. */
const DESTRUCTIVE_CANCEL_LABEL = "Go back";

/**
 * Confirmation dialog for destructive or important actions. `variant="destructive"` is the one to
 * use for a delete: a red verb-first button, a "Go back" ghost, an optional list of what else is
 * affected, and an optional type-to-confirm field that keeps the red button off until it matches.
 *
 * @param props - See {@link FetchConfirmationDialogProps}.
 * @returns The dialog.
 * @throws Never.
 */
export const FetchConfirmationDialog = (props: FetchConfirmationDialogProps): React.ReactElement => {
  const {
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    icon,
    iconBgClass,
    layout = "inline",
    isLoading = false,
    isConfirmDisabled: isConfirmDisabledProp = false,
    zIndexClass = "z-50",
    secondaryAction,
  } = props;

  // Hooks
  const typeToConfirmInputId = useId();

  // States
  const [toggleChecked, setToggleChecked] = useState(false);
  const [typedConfirmation, setTypedConfirmation] = useState("");

  // Derived values
  const isDestructive = props.variant === "destructive";
  const type: DialogType = isDestructive ? "danger" : (props.type ?? "danger");
  const confirmText = isDestructive ? props.confirmLabel : (props.confirmText ?? "Confirm");
  const cancelText = isDestructive ? (props.cancelLabel ?? DESTRUCTIVE_CANCEL_LABEL) : (props.cancelText ?? "Cancel");
  const hideCancel = isDestructive ? false : (props.hideCancel ?? false);
  const emphasis = isDestructive ? "confirm" : (props.emphasis ?? "confirm");
  const emphasizedCancelColor = isDestructive ? "primary" : (props.emphasizedCancelColor ?? "primary");
  const requiresToggle = isDestructive ? false : (props.requiresToggle ?? false);
  const consequences = isDestructive ? (props.consequences ?? []) : [];
  const typeToConfirm = isDestructive ? props.typeToConfirm : undefined;

  const buttonColor = BUTTON_COLOR_MAP[type];
  const isTypedConfirmationMatched = !typeToConfirm || typedConfirmation.trim() === typeToConfirm.trim();
  const isConfirmDisabled =
    isLoading || isConfirmDisabledProp || (requiresToggle && !toggleChecked) || !isTypedConfirmationMatched;
  const isCancelEmphasized = emphasis === "cancel";
  // The destructive way back is a ghost: the red button is the only filled thing in the footer.
  const restingCancelVariant = isDestructive ? "ghost" : "outline";

  // Handlers
  const handleConfirm = async (): Promise<void> => {
    if (isConfirmDisabled) return;
    if ((await onConfirm()) === false) return;
    onClose();
  };

  const handleTypedConfirmationKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    void handleConfirm();
  };

  // Effects: join the shared modal stack while open, so Escape here does not also close a
  // FetchModal this dialog was opened from. `onClose` is read through a ref so a fresh inline
  // handler never re-pushes this entry above a dialog opened on top of it.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!isOpen) return;
    const closeEntry = (): void => onCloseRef.current();
    modalStack.push(closeEntry);
    return () => modalStack.remove(closeEntry);
  }, [isOpen]);

  // Effects: reset the toggle and the typed confirmation when the dialog closes
  useEffect(() => {
    if (isOpen) return;
    setToggleChecked(false);
    setTypedConfirmation("");
  }, [isOpen]);

  // Render
  const confirmButton = (
    <FetchButton
      variant={isCancelEmphasized ? "outline" : "primary"}
      color={isCancelEmphasized ? "neutral" : buttonColor}
      size="sm"
      onClick={handleConfirm}
      disabled={isConfirmDisabled}
      loading={isLoading}
      className="whitespace-nowrap"
    >
      {confirmText}
    </FetchButton>
  );

  const cancelButton = (
    <FetchButton
      variant={isCancelEmphasized ? "primary" : restingCancelVariant}
      color={isCancelEmphasized ? (emphasizedCancelColor === "semantic" ? buttonColor : "primary") : "neutral"}
      size="sm"
      onClick={onClose}
      disabled={isLoading}
      className="whitespace-nowrap"
    >
      {cancelText}
    </FetchButton>
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className={`relative ${zIndexClass}`} onClose={isLoading ? () => {} : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                <div className="p-6">
                  {layout === "stacked" ? (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        aria-label="Close"
                        className="absolute right-0 top-0 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-500"
                      >
                        <X className="h-5 w-5" />
                      </button>
                      <div className="flex items-center gap-3 pr-8">
                        {icon && (
                          <div
                            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${iconBgClass ?? ICON_BG_BY_TYPE[type]}`}
                          >
                            {icon}
                          </div>
                        )}
                        <Dialog.Title as="h3" className="min-w-0 break-words text-lg font-semibold text-gray-900">
                          {title}
                        </Dialog.Title>
                      </div>
                      <Dialog.Description as="div" className="mt-3 text-sm font-medium text-gray-500">
                        {message}
                      </Dialog.Description>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-3">
                      {icon && (
                        <div
                          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${iconBgClass ?? ICON_BG_BY_TYPE[type]}`}
                        >
                          {icon}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <Dialog.Title as="h3" className="min-w-0 break-words text-lg font-semibold text-gray-900">
                          {title}
                        </Dialog.Title>
                        <Dialog.Description as="div" className="mt-1 text-sm font-medium text-gray-500">
                          {message}
                        </Dialog.Description>
                      </div>
                      <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        aria-label="Close"
                        className="flex-shrink-0 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-500"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  )}

                  {consequences.length > 0 && (
                    <ul
                      aria-label="What else this affects"
                      className="mt-4 flex list-none flex-col gap-1 text-sm font-medium text-gray-700"
                    >
                      {consequences.map((consequence) => (
                        <li key={consequence}>{consequence}</li>
                      ))}
                    </ul>
                  )}

                  {typeToConfirm && (
                    <div className="mt-5 flex flex-col gap-2">
                      <label htmlFor={typeToConfirmInputId} className="text-sm font-medium text-gray-700">
                        Type <span className="font-semibold text-gray-900">{typeToConfirm}</span> to confirm
                      </label>
                      <FetchInput
                        id={typeToConfirmInputId}
                        value={typedConfirmation}
                        onChange={(event) => setTypedConfirmation(event.target.value)}
                        onKeyDown={handleTypedConfirmationKeyDown}
                        autoComplete="off"
                        spellCheck={false}
                        disabled={isLoading}
                      />
                    </div>
                  )}

                  {requiresToggle && (
                    <div className="mt-5">
                      <FetchToggle
                        checked={toggleChecked}
                        onChange={setToggleChecked}
                        size="sm"
                        color="destructive"
                        label="I understand this action cannot be undone"
                      />
                    </div>
                  )}
                </div>

                {/* Emphasized (filled) button sits on the right. Labels never wrap; when a row runs
                    out of room the buttons move to a new line instead. */}
                <div className="flex flex-wrap justify-end gap-3 px-6 pb-6">
                  {secondaryAction && (
                    <FetchButton
                      variant="ghost"
                      color="neutral"
                      size="sm"
                      onClick={secondaryAction.onClick}
                      disabled={isLoading}
                      className="whitespace-nowrap"
                    >
                      {secondaryAction.label}
                    </FetchButton>
                  )}
                  {isCancelEmphasized ? (
                    <>
                      {confirmButton}
                      {!hideCancel && cancelButton}
                    </>
                  ) : (
                    <>
                      {!hideCancel && cancelButton}
                      {confirmButton}
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
